import { Injectable, NgZone } from "@angular/core";
import { Platform, NavController } from '@ionic/angular';
import { AndroidPermissions, AndroidPermissionResponse } from '@ionic-native/android-permissions/ngx';
declare var PhoneCallTrap: any; // cordova plugin
import { Call } from '../model/call.interface';
import { AuthService } from './auth.service';
import { LeadsRepositoryBUT } from './leads.repository.but';
import { AppService } from './app.service';
import { Lead } from '../model/lead.model';

@Injectable()
export class CallService {
    private prevState = "";
    private state = "";
    private oldCalls: string[] = [];

    constructor(
        private readonly platform: Platform,
        private readonly androidPermissions: AndroidPermissions,
        private readonly authService: AuthService,
        private readonly leadsRepository: LeadsRepositoryBUT,
        private readonly appService: AppService,
        private readonly ngZone: NgZone,
        private readonly navCtrl: NavController,
    ) { }

    get canWork(): boolean { return this.platform.is("android") && (!this.platform.is('mobileweb')) }

    public async init(): Promise<void> {
        if (this.canWork) {
            if (await this.setPermissions()) {
                this.initCallProcessing();
                (<any>window).drawover.initialize(() => { }, () => { }, {});
                let strOldCalls: string = localStorage.getItem("oldCalls");

                if (strOldCalls) {
                    try {
                        const parsed = JSON.parse(strOldCalls);
                        this.oldCalls = Array.isArray(parsed) ? parsed : [];
                    } catch (_err) {
                        this.oldCalls = [];
                        localStorage.removeItem("oldCalls");
                    }
                }
            }
        }
    }

    private async setPermissions(): Promise<boolean> {
        const values = await Promise.all([
            this.setPermission(this.androidPermissions.PERMISSION.READ_PHONE_STATE),
            this.setPermission(this.androidPermissions.PERMISSION.READ_CALL_LOG)
        ]);
        return values.every(i => i);
    }

    private async setPermission(permission: any): Promise<boolean> {
        let res: AndroidPermissionResponse = await this.androidPermissions.checkPermission(permission);

        if (res.hasPermission) {
            return true;
        }

        let res2: AndroidPermissionResponse = await this.androidPermissions.requestPermission(permission);
        return res2.hasPermission;
    }

    private initCallProcessing(): void {
        if (PhoneCallTrap) {
            PhoneCallTrap.onCall(async obj => {
                if (this.authService.authenticated) {
                    this.prevState = this.state;
                    let call: Call;
                    try {
                        call = JSON.parse(obj);
                    } catch (_err) {
                        return;
                    }
                    if (!call || !call.incomingNumber) {
                        return;
                    }
                    this.state = call.state;

                    if (call.incomingNumber.includes('+')) {
                        call.incomingNumber = call.incomingNumber.replace('+', '');
                    }

                    let lead: Lead | null = await this.findLead(call.incomingNumber);

                    if (this.state === "RINGING") { // incoming call

                        if (lead) {
                            this.showCallReceiveDrawOver(lead, call.incomingNumber);
                        } else {
                            this.showCallEndDrawOver(call.incomingNumber);
                        }
                    } else if (this.state === "IDLE") {
                        if (this.prevState === "RINGING") { // call ended without answer
                            if (lead) {
                                this.showCallReceiveDrawOver(lead, call.incomingNumber);
                            } else {
                                this.showCallEndDrawOver(call.incomingNumber);
                            }
                        } else if (this.prevState === 'OFFHOOK') { // call ended with answer
                            if (!this.oldCalls.includes(call.incomingNumber)) {
                                if (lead) {
                                    this.showCallReceiveDrawOver(lead, call.incomingNumber);
                                } else {
                                    this.showCallEndDrawOver(call.incomingNumber);
                                }
                                this.oldCalls.push(call.incomingNumber);
                                localStorage.setItem("oldCalls", JSON.stringify(this.oldCalls));
                            }
                        }
                    }
                }
            });
        }
    }

    private findLead(phone: string): Promise<Lead | null> {
        return new Promise((resolve, reject) => {
            this.leadsRepository.ready.subscribe(r => {
                if (r) {
                    this.tryLoadLead(phone, 1).then(resolve);
                }
            });
        });
    }

    // This lookup runs automatically in the background on every native call state change
    // (not user-initiated) — a failure here must never surface a UI error or force a logout,
    // since it would block the user from the app right after finishing a call (log only).
    // A single retry guards against momentary/transient failures so a real lead isn't
    // misreported as "not found" (which would show the wrong "add this phone?" prompt).
    private tryLoadLead(phone: string, retriesLeft: number): Promise<Lead | null> {
        return new Promise(resolve => {
            this.leadsRepository.loadLeads(0, 1, phone, 0, "followUp", 0).subscribe(res => {
                if (res.responseCode === 200) {
                    if (res.data.leads.length) {
                        resolve(new Lead().build(res.data.leads[0]));
                    } else {
                        resolve(null);
                    }
                } else if (retriesLeft > 0) {
                    console.error(`CallService findLead API error, retrying: ${res.responseCode} ${res.error || res.Error}`);
                    setTimeout(() => this.tryLoadLead(phone, retriesLeft - 1).then(resolve), 1000);
                } else {
                    console.error(`CallService findLead API error: ${res.responseCode} ${res.error || res.Error}`);
                    resolve(null);
                }
            }, err => {
                if (retriesLeft > 0) {
                    console.error(`CallService findLead HTTP error, retrying: ${this.appService.getErrorMessage(err)}`);
                    setTimeout(() => this.tryLoadLead(phone, retriesLeft - 1).then(resolve), 1000);
                } else {
                    console.error(`CallService findLead HTTP error: ${this.appService.getErrorMessage(err)}`);
                    resolve(null);
                }
            });
        });
    }

    // using Dhaval plugin
    private showCallReceiveDrawOver(lead: Lead, phoneNumber: string): void {
        (<any>window).drawover.callReceive(
            () => {
                this.ngZone.run(() => {
                    (<any>window).plugins.bringtofront();
                    this.navCtrl.navigateForward(`/leadClients/${lead.clientId}`);
                });
            },
            (error) => { },
            { phoneNumber, name: lead.fullname }
        );
    }

    // using Dhaval plugin
    private showCallEndDrawOver(phoneNumber: string): void {
        (<any>window).drawover.callEnd(
            () => {
                this.ngZone.run(() => {
                    (<any>window).plugins.bringtofront();
                    this.navCtrl.navigateForward(`/leadClients/new/fromcalls/${phoneNumber}`);
                });
            },
            (error) => { },
            { phoneNumber }
        );
    }
}
